import type { Campaign, CampaignQuestState } from "@/components/QuestsV2/types";
import { DiscordLogo, SteamLogo } from "@/components/icons";
import TelegramIcon from "@mui/icons-material/Telegram";
import XIcon from "@mui/icons-material/X";
import { Gamepad2, Globe, Heart, Plus, SquarePlay, Trophy } from "lucide-react";

interface QuestAPIResponse {
  campaign_points: number;
  external_id: string;
  game_id: string;
  game_name: string;
  gems: number;
  id: number;
  instant_distribution: boolean;
  name: string;
  perpetual_rewards_eligible: boolean;
  refresh_period_hours: number | null;
  type: string;
  valid_from: string | null;
  valid_to: string | null;
  participant_count: number | null;
  categories: string[];
  content_metadata: string | null;
  metadata: string | null;
}

interface ContentMetadata {
  caption?: string;
  link_url?: string;
}

interface CampaignContentMetadata {
  headline?: string;
  tagline?: string; // Supports markdown-like syntax: [text](url)
  xHandle?: string;
  rules?: string;
  campaign_ended_message?: string;
  prize_distributed?: boolean;
}

interface QuestMetadata {
  target_tweet_id?: string;
  tweet_text?: string;
  tweet_username?: string;
  tweet_requires_image?: boolean;
  tweet_caption?: string;
  tweet_draft_text?: string;
  tweet_placeholder?: string;
  tweet_substring?: string;
  action_type?: "download" | "pre-reg";
  // The other metadata from telegram and discord, etc
  // will be here
}

interface GetQuestsAPIResponse {
  results: QuestAPIResponse[];
}

interface GameAPIResponse {
  game_id: string;
  name: string;
  slug: string;
  logo_url: string;
}

interface SocialStatAPIResponse {
  count: number;
  handle?: string;
  type: string;
}

interface GetGameDetailsAPIResponse {
  game_id: string;
  name: string;
  landing_page_url: string;
  logo_url: string;
  background_url: string;
  splash_urls: string[];
  carousel_urls: string[];
  genres: string[];
  social_stats: SocialStatAPIResponse[];
  slug: string;
  content_metadata: string;
}

interface GameContentMetadata {
  carousel_description: string;
}

interface CampaignAPIResponse {
  created_at: string;
  end_at: string;
  external_id: string;
  game_id: string;
  id: number;
  name: string;
  start_at: string;
  updated_at: string;
  type: "creator" | "standard";
  win_type: string;
  win_value: number;
  content_metadata?: string;
}

interface CampaignsAPIResponse {
  result: CampaignAPIResponse[];
}

interface GameData {
  details: GetGameDetailsAPIResponse;
  gameContent: GameContentMetadata | undefined;
  quests: CampaignQuestState[];
  badges: CampaignQuestState[];
  campaigns: Campaign[];
}

const formatterWithPrecision = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
  maximumFractionDigits: 1, // Display one decimal place
});

const formatQuestNumber = (number: number) => {
  return formatterWithPrecision.format(number);
};

const getQuestIcon = (questType: string, categories: string[]) => {
  let type = questType;
  if (type === "social_engagement") {
    if (categories.includes("social_telegram_engage")) {
      type = "telegram_engagement";
    } else if (categories.includes("social_twitter_engage")) {
      type = "x_engagement";
    } else if (categories.includes("social_discord_engage")) {
      type = "discord_engagement";
    }
  }
  switch (type) {
    case "in_game":
      return Gamepad2;
    case "wishlist":
      return Heart;
    case "trailer":
      return SquarePlay;
    case "post_x_tweet":
    case "follow_x_account":
    case "retweet_and_like":
    case "x_engagement":
      return XIcon;
    case "join_telegram_chat":
    case "telegram_engagement":
      return TelegramIcon;
    case "join_discord_server":
    case "discord_engagement":
      return DiscordLogo;
    case "app_store_image_validation":
      return Plus;
    case "steam_wishlist":
      return SteamLogo;
    case "website":
    case "no_validate":
      return Globe;
    default:
      return Trophy;
  }
};

const getQuestButtonText = (questType: string) => {
  switch (questType) {
    case "wishlist":
      return "Follow";
    case "in_game":
      return "Play";
    case "trailer":
      return "Watch";
    case "retweet_and_like":
      return "Repost";
    case "join_telegram_chat":
      return "Join";
    case "join_discord_server":
      return "Join";
    case "social_engagement":
      return "Engage";
    case "post_x_tweet":
      return "Post on X";
    case "app_store_image_validation":
      return "Add Image";
    case "steam_wishlist":
      return "Add to Steam Wishlist";
    default:
      return "Start";
  }
};

const isXPrimative = (questType: string) => {
  return (
    questType === "retweet_and_like" ||
    questType === "post_x_tweet" ||
    questType === "follow_x_account"
  );
};

export type {
  QuestAPIResponse,
  GetQuestsAPIResponse,
  GameAPIResponse,
  SocialStatAPIResponse,
  GetGameDetailsAPIResponse,
  CampaignAPIResponse,
  CampaignsAPIResponse,
  GameData,
  ContentMetadata,
  CampaignContentMetadata,
  QuestMetadata,
  GameContentMetadata,
};
export { formatQuestNumber, getQuestIcon, getQuestButtonText, isXPrimative };
